import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { Link, useNavigate } from 'react-router'

import { useAdminExtensions, useLocales } from '../../extensions/hooks'
import { pluginOf } from '../../extensions/registry'
import { PluginFrame } from '../../extensions/slots'
import {
  Button,
  EmptyState,
  Loading,
  PageHeader,
  Panel,
  StatusBadge,
} from '../../shared/components'
import type { Messages } from '../../shared/i18n'
import { languageName, useT } from '../../shared/i18n'
import { formatBytes, formatDateTime, formatRelative } from '../../shared/utils'
import { useAuthStore, useCan } from '../auth/hooks'
import {
  useEntries,
  useEntryLocaleStats,
  useEntryStats,
} from '../content-entries/hooks'
import { useMediaStats } from '../media/hooks'

import type { StatTileProps } from './dashboard.types'

function greeting(t: Messages) {
  const h = new Date().getHours()

  return h < 12
    ? t.dashboard.morning
    : h < 18
      ? t.dashboard.afternoon
      : t.dashboard.evening
}

/** A figure that opens its list; zero is quiet, `attention` marks one above zero (ADR-079). */
function StatTile({ to, label, value, hint, attention }: StatTileProps) {
  const tone =
    value === null || value === 0
      ? ' stat--quiet'
      : attention
        ? ' stat--attention'
        : ''

  return (
    <Link to={to} className={`stat stat--link${tone}`}>
      <p className="stat__label">{label}</p>
      <p className="stat__value">{value ?? '…'}</p>
      {hint && <p className="stat__hint">{hint}</p>}
    </Link>
  )
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user)
  const { t, locale } = useT()
  const canReadContent = useCan('content.entry.read')
  const canCreate = useCan('content.entry.create')
  const canReadMedia = useCan('media.read')
  const navigate = useNavigate()
  const stats = useEntryStats()
  const { locales } = useLocales()
  const byLocale = useEntryLocaleStats(canReadContent && locales.length > 1)
  const media = useMediaStats()
  const recent = useEntries({ limit: 8, sort: '-updatedAt' }, canReadContent)
  const review = useEntries({ status: 'IN_REVIEW', limit: 5 }, canReadContent)

  const scheduled = useEntries(
    { status: 'SCHEDULED', limit: 5, sort: 'updatedAt' },
    canReadContent,
  )

  const extensions = useAdminExtensions()

  const s = stats.data ?? {}
  const total = Object.values(s).reduce((a, b) => a + b, 0)

  return (
    <>
      <PageHeader
        kicker={greeting(t)}
        title={user?.displayName ?? t.nav.dashboard}
        lead={t.dashboard.lead}
        actions={
          canCreate && (
            <Button
              variant="primary"
              icon={faPlus}
              onClick={() => navigate('/content')}
            >
              {t.dashboard.newEntry}
            </Button>
          )
        }
      />
      {canReadContent && (
        <div className="stat-grid" style={{ marginBottom: 40 }}>
          <StatTile
            to="/content"
            label={t.dashboard.entries}
            value={stats.isLoading ? null : total}
          />
          <StatTile
            to="/content?status=PUBLISHED"
            label={t.dashboard.published}
            value={s.PUBLISHED ?? 0}
          />
          <StatTile
            to="/content?status=DRAFT"
            label={t.dashboard.drafts}
            value={s.DRAFT ?? 0}
          />
          <StatTile
            to="/content?status=IN_REVIEW"
            label={t.dashboard.inReview}
            value={s.IN_REVIEW ?? 0}
            attention
          />
          <StatTile
            to="/content?status=SCHEDULED"
            label={t.dashboard.scheduled}
            value={s.SCHEDULED ?? 0}
          />
          {canReadMedia && (
            <StatTile
              to="/media"
              label={t.nav.media}
              value={Object.values(media.data?.byType ?? {}).reduce(
                (a, b) => a + b,
                0,
              )}
              hint={formatBytes(media.data?.totalBytes ?? 0)}
            />
          )}
        </div>
      )}

      {canReadContent && byLocale.data && (
        <div style={{ marginBottom: 40 }}>
          <p className="section-title">{t.dashboard.byLanguage}</p>
          <div className="stat-grid">
            {locales.map((l) => (
              <StatTile
                key={l}
                to={`/content?lang=${l}`}
                label={languageName(l, locale)}
                value={byLocale.data[l] ?? 0}
                hint={<span className="mono">{l}</span>}
              />
            ))}
          </div>
        </div>
      )}

      <div className="grid-2">
        {canReadContent && (
          <Panel
            title={t.dashboard.recent}
            actions={
              <Link className="small" to="/content">
                {t.dashboard.viewAll}
              </Link>
            }
            flush
          >
            {recent.isLoading ? (
              <Loading />
            ) : !recent.data?.items.length ? (
              <EmptyState title={t.dashboard.noContent}>
                {t.dashboard.noContentText}
              </EmptyState>
            ) : (
              <table className="table">
                <tbody>
                  {recent.data.items.map((e) => (
                    <tr
                      key={e.id}
                      data-clickable="true"
                      onClick={() => navigate(`/content/${e.id}`)}
                    >
                      <td>
                        <div className="cell-title">
                          {e.title ?? t.common.untitled}
                        </div>
                        <div className="cell-sub">{e.contentType.name}</div>
                      </td>
                      <td>
                        <StatusBadge status={e.status} />
                      </td>
                      <td className="muted cell-actions">
                        {formatRelative(e.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        )}
        <div className="stack">
          {canReadContent && (
            <Panel title={t.dashboard.awaitingReview} flush>
              {!review.data?.items.length ? (
                <p className="panel__body muted small">
                  {t.dashboard.nothingReview}
                </p>
              ) : (
                <table className="table">
                  <tbody>
                    {review.data.items.map((e) => (
                      <tr
                        key={e.id}
                        data-clickable="true"
                        onClick={() => navigate(`/content/${e.id}`)}
                      >
                        <td>
                          <div className="cell-title">
                            {e.title ?? t.common.untitled}
                          </div>
                          <div className="cell-sub">
                            {formatRelative(e.updatedAt)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          )}
          {canReadContent && (
            <Panel title={t.dashboard.scheduled} flush>
              {!scheduled.data?.items.length ? (
                <p className="panel__body muted small">
                  {t.dashboard.noScheduled}
                </p>
              ) : (
                <table className="table">
                  <tbody>
                    {scheduled.data.items.map((e) => (
                      <tr
                        key={e.id}
                        data-clickable="true"
                        onClick={() => navigate(`/content/${e.id}`)}
                      >
                        <td>
                          <div className="cell-title">
                            {e.title ?? t.common.untitled}
                          </div>
                          <div className="cell-sub">
                            {formatDateTime(e.scheduledAt)}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          )}
        </div>
      </div>

      {extensions.dashboardWidgets.length > 0 && (
        <div className="stack" style={{ marginTop: 40 }}>
          <p className="section-title">{t.dashboard.fromPlugins}</p>
          <div
            className="grid-2"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
              // Each card is as tall as its widget: no empty space under a footer.
              alignItems: 'start',
            }}
          >
            {extensions.dashboardWidgets.map((w) => {
              const plugin = pluginOf(extensions, w.pluginKey)

              if (!plugin?.adminUrl) return null

              return (
                <PluginFrame
                  key={`${w.pluginKey}/${w.id}`}
                  pluginKey={plugin.key}
                  pluginName={plugin.name}
                  adminUrl={plugin.adminUrl}
                  kind="dashboard"
                  extensionId={w.id}
                  title={w.title}
                  capabilities={plugin.capabilities}
                  apiPrefixes={plugin.apiPrefixes}
                  initialHeight={w.height}
                  icon={w.icon ?? plugin.icon}
                />
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
